import React from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import SwitchButton from 'core/components/Switch';
import {
  CcmPCCConsentDTO,
  PCCConsentFlagDTO,
} from 'dtos';
import {
  formatCompactConsentMetadata,
  formatCompactEligibilityMetadata,
  formatCompactOptOutMetadata,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/helper';
import type {
  PCCForm,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/types';
import {
  notEmptyString,
} from '@populateui/utils';
import Skeleton from 'core/components/Skeleton';

type Props = {
  consent?: CcmPCCConsentDTO;
  consentFlag?: PCCConsentFlagDTO;
  consentGivenByName: string | null;
  disabled?: boolean;
  isCcmApplicable: boolean;
  isLoading?: boolean;
};

type CompactRowProps = {
  label: string;
  metadata: string;
  name: 'ccm.isEligible' | 'hasConsentedToCommunication' | 'ccm.isOptedOut';
  disabled?: boolean;
};

function CompactRow({
  label,
  metadata,
  name,
  disabled = false,
}: CompactRowProps) {
  const { control } = useFormContext<PCCForm>();

  return (
    <div className="flex flex-col gap-0.5 py-1.5">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-900">
          {label}
        </span>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <SwitchButton
              checked={field.value}
              onChange={(_, value) => { field.onChange(value); }}
              color="primary"
              disabled={disabled}
              classes={{ rootContainer: 'gap-0 -mt-1' }}
            />
          )}
        />
      </div>
      {notEmptyString(metadata) && (
        <span className="text-[11px] text-gray-700">
          {metadata}
        </span>
      )}
    </div>
  );
}

function CompactRowSkeleton() {
  return (
    <div className="flex flex-col gap-0.5 py-1.5">
      <div className="flex items-center gap-2">
        <Skeleton className="!h-4 !w-36" />
      </div>
      <Skeleton className="!h-3 !w-48" />
    </div>
  );
}

export default function ConsentCompactSection({
  consent,
  consentFlag,
  consentGivenByName,
  disabled = false,
  isCcmApplicable,
  isLoading = false,
}: Props) {
  return (
    <div className="flex flex-col w-full mb-4">
      <h4 className="font-semibold text-xs leading-4 mb-1">
        PCC
      </h4>
      <div className="">
        {isLoading ? (
          <>
            <CompactRowSkeleton />
            {isCcmApplicable && (
              <>
                <CompactRowSkeleton />
                <CompactRowSkeleton />
              </>
            )}
          </>
        ) : (
          <>
            <CompactRow
              label="Consented to Communication"
              metadata={formatCompactConsentMetadata(consentFlag, consentGivenByName)}
              name="hasConsentedToCommunication"
              disabled={disabled}
            />
            {isCcmApplicable && (
              <>
                <CompactRow
                  label="Eligible for CCM"
                  metadata={formatCompactEligibilityMetadata(consent)}
                  name="ccm.isEligible"
                  disabled={disabled}
                />
                <CompactRow
                  label="Opted Out"
                  metadata={formatCompactOptOutMetadata(consent)}
                  name="ccm.isOptedOut"
                  disabled={disabled}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
